/**
 * Sentry 懒加载器
 * 用于延迟加载 Sentry,减少首屏 bundle 体积。
 *
 * 整体思路分成 4 层：
 * 1. 入口阶段先挂一个“占位版 sentry stub”，保证业务代码可以立即调用 window.sentry。
 * 2. 在真实 Sentry chunk 尚未加载完成前，把用户信息和业务上报请求先暂存在内存里。
 * 3. 同时用全局 error / unhandledrejection 监听，兜住浏览器层面的早期异常。
 * 4. 等真实模块加载完成后，再统一初始化、回放队列、清空早期缓存。
 *
 * 这样做的核心目标是：
 * 1. 首屏不把 Sentry 大包塞进主 bundle。
 * 2. 异步加载窗口期内，也尽量不丢错误和用户上下文。
 */

// 真实 Sentry 模块的加载 Promise。
// 这样多次调用 loadSentry() 时会复用同一个 Promise，避免重复发起 import 请求。
let sentryPromise = null;

// 动态 import('./sentry') 成功后的模块引用。
// 它主要用来表示“真实模块是否已经回来”，从而决定是否还需要继续收集早期错误。
let sentryModule = null;

// 真实 Sentry 是否已经执行过 init。
// 虽然 sentry.js 里本身也做了幂等保护，但这里再挡一层，可以让 loader 的行为更明确。
let sentryInitialized = false;

// 业务主动调用 window.sentry.trackReport(...) 时，如果真实模块还没回来，
// 上报内容会先排进这个队列，等真实模块 ready 之后再逐条补发。
let queuedReports = [];

// 用户信息只保留“最后一次”设置结果，不做数组队列。
// 原因是 setUser 更像是状态写入，而不是事件流；最新一次通常才是我们真正想要的值。
let queuedUser = null;

// 给 stub 打一个私有标记，便于区分当前 window.sentry 到底是“占位对象”还是真实对象。
const SENTRY_STUB_FLAG = '__SENTRY_ASYNC_STUB__';

// 早期错误队列（在真实 Sentry 还没加载完成前，由浏览器全局监听器收集到的异常）。
// 挂在 window 上而不是模块局部变量，是为了避免模块被重复执行时丢失已有缓存。
window.earlyErrors = window.earlyErrors || [];

/**
 * 创建一个“占位版 sentry 对象”。
 *
 * 这个对象的职责不是立即上报，而是：
 * 1. 对外保持和 window.sentry 尽量一致的调用方式。
 * 2. 在真实模块尚未加载时，先把数据缓存起来。
 * 3. 首次被调用时顺便触发真实 Sentry 的异步加载。
 *
 * 这样业务层就不需要到处写：
 * if (window.sentry) { ... }
 * if (SentryLoaded) { ... }
 *
 * 而是可以统一调用 window.sentry.xxx(...)。
 */
const createSentryStub = () => ({
  [SENTRY_STUB_FLAG]: true,

  // 占位版 init 不会真的初始化 SDK。
  // 它的作用只是把调用转交给 loadSentry，让外部继续沿用 init 这个心智模型。
  init() {
    return loadSentry();
  },

  // 在真实 SDK 尚未准备好之前，不可能返回有效 client。
  // 这里返回 null，表示“当前还没有真实客户端实例”。
  getClient() {
    return null;
  },

  // 用户信息在初始化完成后只需要保留最新一份，所以这里直接覆盖 queuedUser。
  // 例如：游客态 -> 登录态，最终我们只关心登录后的用户身份。
  setUser(user) {
    queuedUser = user;
  },

  // 业务手动上报错误时，先把 payload 放进队列里，再异步触发真实模块加载。
  // 注意这里故意不 await：
  // 1. 避免调用方被迫感知 Promise。
  // 2. 保持 window.sentry.trackReport(...) 现有调用方式不变。
  trackReport(payload) {
    if (payload) {
      queuedReports.push(payload);
    }
    loadSentry().catch(() => {});
  },

  // 以下几个方法都是“代理方法”：
  // 先确保真实模块被加载，再把调用转发给真实 sentry。
  // 之所以保留这些同名方法，是为了减少旧代码改造成本。
  manualReport(clearCache) {
    return loadSentry().then(() => window.sentry.manualReport(clearCache));
  },
  directReport(clearCache) {
    return loadSentry().then(() => window.sentry.directReport(clearCache));
  },
  throttledReport(clearCache) {
    return loadSentry().then(() => window.sentry.throttledReport(clearCache));
  },

  // 在真实 SDK 没回来前，只能基于本地队列给一个“近似统计值”。
  // 这里的 total / unreported 都表示“当前 loader 手里攒着多少待处理数据”。
  // reported 固定为 0，因为真正的上报行为还没有发生。
  getPendingErrorsInfo() {
    return {
      total: queuedReports.length + window.earlyErrors.length,
      unreported: queuedReports.length + window.earlyErrors.length,
      reported: 0
    };
  }
});

if (!window.sentry || window.sentry[SENTRY_STUB_FLAG]) {
  window.sentry = createSentryStub();
}

/**
 * 初始化真实 Sentry。
 *
 * 这里调用的是 sentry.js 里挂到 window.sentry 上的真实 init 方法。
 * 由于 loader 可能被多个入口同时触发，所以这里先做一次前置幂等保护，
 * 避免重复初始化带来的副作用，比如重复注册集成、重复开启定时器等。
 */
function initSentry() {
  if (sentryInitialized || !window.sentry || !window.sentry.init) {
    return;
  }
  window.sentry.init();
  sentryInitialized = true;
}

/**
 * 回放缓存的用户信息。
 *
 * 为什么单独拆出来：
 * 1. 用户信息和错误事件的语义不同。
 * 2. 用户信息更像“当前状态”，只需要最新值。
 * 3. 在补发 queuedReports 之前先 setUser，可以让后续错误尽量带上正确用户上下文。
 */
function flushQueuedUser() {
  if (!queuedUser || !window.sentry || !window.sentry.setUser) {
    return;
  }
  window.sentry.setUser(queuedUser);
  queuedUser = null;
}

/**
 * 回放业务手动排队的上报请求。
 *
 * 这些数据通常来自：
 * 1. request.js 里的接口异常上报
 * 2. AppView 错误边界里的 componentDidCatch
 * 3. 其他业务主动调用 window.sentry.trackReport 的地方
 *
 * 这里先把原数组引用取出来，再把 queuedReports 立即置空，
 * 是为了避免回放过程中如果又来了新的错误，旧队列和新队列互相污染。
 */
function flushQueuedReports() {
  if (!queuedReports.length || !window.sentry || !window.sentry.trackReport) {
    return;
  }

  const reports = queuedReports;
  queuedReports = [];

  reports.forEach((report) => {
    window.sentry.trackReport(report);
  });
}

/**
 * 回放浏览器全局监听器收集到的“早期异常”。
 *
 * 这类异常和 queuedReports 的差别在于：
 * 1. queuedReports 来自业务主动调用。
 * 2. earlyErrors 来自浏览器层面的被动捕获。
 *
 * 常见场景包括：
 * 1. 页面刚启动时的同步脚本报错
 * 2. Promise 未处理拒绝
 * 3. 真实 Sentry chunk 尚未下载完成前出现的运行时异常
 */
function flushEarlyErrors() {
  if (
    !window.earlyErrors ||
    !window.earlyErrors.length ||
    !window.sentry ||
    !window.sentry.trackReport
  ) {
    return;
  }

  console.log(`[Sentry] 上报 ${window.earlyErrors.length} 个早期错误`);

  window.earlyErrors.forEach((earlyError) => {
    // 浏览器 error 事件里通常能拿到更完整的文件名、行列号信息，
    // 所以这里会把这些上下文补进 extra，便于后面排查首屏阶段的问题。
    if (earlyError.type === 'error') {
      window.sentry.trackReport({
        error: earlyError.error || new Error(earlyError.message),
        extra: {
          filename: earlyError.filename,
          lineno: earlyError.lineno,
          colno: earlyError.colno,
          capturedAt: 'early-error-listener',
          timestamp: earlyError.timestamp
        }
      });
    } else if (earlyError.type === 'unhandledrejection') {
      // Promise reject 的 reason 不一定是 Error 对象，
      // 所以这里统一兜底转成 Error，避免后续真实上报链路处理失败。
      window.sentry.trackReport({
        error:
          earlyError.reason instanceof Error
            ? earlyError.reason
            : new Error(String(earlyError.reason)),
        extra: {
          capturedAt: 'early-unhandledrejection-listener',
          timestamp: earlyError.timestamp
        }
      });
    }
  });

  window.earlyErrors = [];
}

// 全局错误监听只注册一次。
// loader 作为入口模块有可能因为不同打包边界被重复执行，window 标记可以避免重复 addEventListener。
if (!window.sentryErrorListenerAdded) {
  window.sentryErrorListenerAdded = true;

  window.addEventListener('error', (event) => {
    // 只在真实模块尚未加载完成时收集。
    // 一旦 sentryModule 已存在，后续异常应交给真实 SDK 自己处理，避免重复记录。
    if (!sentryModule && window.earlyErrors) {
      window.earlyErrors.push({
        type: 'error',
        error: event.error,
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: Date.now()
      });
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Promise 未处理拒绝与普通 error 分开记录，
    // 方便后续在 flush 时做不同的错误对象转换。
    if (!sentryModule && window.earlyErrors) {
      window.earlyErrors.push({
        type: 'unhandledrejection',
        reason: event.reason,
        timestamp: Date.now()
      });
    }
  });
}

/**
 * 加载真实 Sentry 模块。
 *
 * 这是整个 loader 的核心入口，负责做 5 件事：
 * 1. 复用已有 Promise，避免重复 import。
 * 2. 动态加载 ./sentry，把大包拆出主 bundle。
 * 3. 加载成功后执行真实初始化。
 * 4. 把 stub 阶段缓存的用户信息、业务上报、早期异常全部回放。
 * 5. 如果加载失败，重置 sentryPromise，给下次重试留下机会。
 *
 * @returns {Promise} Sentry 模块
 */
export function loadSentry() {
  if (sentryPromise) return sentryPromise;

  sentryPromise = import(/* webpackChunkName: "sentry" */ './sentry')
    .then((module) => {
      sentryModule = module;
      initSentry();
      flushQueuedUser();
      flushQueuedReports();
      flushEarlyErrors();

      return module;
    })
    .catch((error) => {
      console.error('[Sentry] 加载失败:', error);
      // 失败后把 Promise 清掉，避免后续所有调用都复用一个已经 reject 的 Promise。
      // 这样下次再触发 loadSentry() 时，仍然可以重新尝试加载。
      sentryPromise = null;
      throw error;
    });

  return sentryPromise;
}

/**
 * 确保 Sentry 已加载。
 *
 * 语义上它比 loadSentry 更偏“调用方立场”：
 * 调用者不一定关心怎么加载，只关心“这里开始我需要一个可用的真实 Sentry”。
 *
 * 目前它只是对 loadSentry 的语义包装，主要用于错误边界、
 * 首次关键异常场景等“希望立即拉起 Sentry”的调用点。
 *
 * @returns {Promise} Sentry 模块
 */
export function ensureSentry() {
  return loadSentry();
}

// 首屏加载完成后自动加载 Sentry。
// 这里的策略不是“立刻加载”，而是“尽量不影响首屏渲染完成”。
if (typeof window !== 'undefined') {
  // 优先使用 requestIdleCallback，把 Sentry 下载安排到浏览器空闲期。
  // timeout: 3000 表示即使主线程一直比较忙，最多也等 3 秒就开始加载。
  const scheduleLoad = () => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(
        () => {
          loadSentry();
        },
        { timeout: 3000 }
      );
    } else {
      // 老浏览器没有 requestIdleCallback 时，退化成一个固定延时策略。
      // 这里不立刻加载，仍然是为了尽量把资源请求放到首屏关键链路之后。
      setTimeout(loadSentry, 2000);
    }
  };

  // 如果页面已经 complete，说明资源加载阶段基本结束，可以直接进入调度。
  // 否则等 window load 后再安排，进一步减少和首屏资源竞争。
  if (document.readyState === 'complete') {
    scheduleLoad();
  } else {
    window.addEventListener('load', scheduleLoad);
  }
}



// WEBPACK FOOTER //
// ./src/utils/lazyLoad/sentryLoader.js